import { AxiosError } from 'axios';
import { PropertySnapshot } from '@th/shared/src/types/PropertySnapshot';
import { Place } from '@th/shared/src/types/Place';
import { useQuery } from '@th/react-query';
import logAxiosError from '../../utils/logAxiosError';
import AxiosClient from '../../utils/AxiosClient';
import useDebounce from '../useDebounce';
import isDefinedAndNonNull from '@th/shared/src/utils/isDefinedAndNonNull';

type SearchSuggestions = {
  nlpPhrase: null | string;
  places: Array<Place>;
  properties: Array<PropertySnapshot>;
  addresses: Array<Place>;
  isFetching: boolean;
};

const ADDRESS_RESULTS_LIMIT = 3;
const NEIGHBORHOOD_RESULTS_LIMIT = 1;
const AREA_RESULTS_LIMIT = 1;
const POSTCODES_RESULTS_LIMIT = 3;

export default function useFetchDebouncedSearchSuggestions(
  searchText: string,
  options: {
    keepPreviousData?: boolean;
  } = {},
): SearchSuggestions {
  const debouncedSearchText = useDebounce(searchText);

  const endpoint = `/api/searchSuggestions?phrase=${encodeURIComponent(
    debouncedSearchText,
  )}`;

  const { data, isFetching } = useQuery(
    ['fetchSearchSuggestions', debouncedSearchText],
    async () => {
      return AxiosClient.get(endpoint);
    },
    {
      enabled: debouncedSearchText.length > 3,
      refetchOnWindowFocus: false,
      keepPreviousData: isDefinedAndNonNull(options.keepPreviousData)
        ? options.keepPreviousData
        : false, // default to false
      onError: (err: AxiosError) => {
        logAxiosError(
          `useFetchDebouncedSearchSuggestions GET ${endpoint}`,
          err,
        );
      },
    },
  );

  const properties = (data?.data.propertySnapshots || []).map(
    PropertySnapshot.fromObject,
  );
  const addresses = (data?.data.addresses || [])
    .slice(0, ADDRESS_RESULTS_LIMIT)
    .map(Place.fromObject);
  const neighborhoods = (data?.data.neighborhoods || [])
    .slice(0, NEIGHBORHOOD_RESULTS_LIMIT)
    .map(Place.fromObject);
  const areas = (data?.data.areas || [])
    .slice(0, AREA_RESULTS_LIMIT)
    .map(Place.fromObject);
  const postcodes = (data?.data.postcodes || [])
    .slice(0, POSTCODES_RESULTS_LIMIT)
    .map(Place.fromObject);
  const otherPlaces = (data?.data.otherPlaces || []).map(Place.fromObject);

  const places = [
    ...addresses,
    ...neighborhoods,
    ...areas,
    ...postcodes,
    ...otherPlaces,
  ];

  const hasSearchResults = [...places, ...properties].length > 0;
  const hasNoResultsAfterSearch = !hasSearchResults && !isFetching;
  const hasVeryFewSearchResults = [...places, ...properties].length <= 2;
  const showAiSearch =
    debouncedSearchText.length > 5 &&
    (hasNoResultsAfterSearch || hasVeryFewSearchResults);

  return {
    nlpPhrase: showAiSearch ? searchText : null,
    places,
    properties,
    addresses,
    isFetching,
  };
}
